import React, { useState, useEffect } from 'react';
import { Coins, CreditCard, Clock, Wrench, ShieldCheck, ArrowRight, X, CheckCircle2 } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';

interface BenefitDetail {
  num: string;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  points: string[];
  recommendation: string;
}

export const BenefitsSection: React.FC = () => {
  const [selectedBenefit, setSelectedBenefit] = useState<BenefitDetail | null>(null);

  const benefitCards: BenefitDetail[] = [
    {
      num: '01',
      title: '투자금 0원',
      subtitle: '초기 비용 0원 · 20년 고정 임대료',
      icon: Coins,
      points: [
        '시공비 · 인허가비 · 설계비 전액 0원',
        '20년 고정 임대료 보장 (경기 무관)',
        '설비 고장 및 자연재해 100% 보험 보상',
        '20년 후 설비 양도양수 또는 원상복구 선택',
      ],
      recommendation: '자본 투자 없이 유휴 지붕으로 안전한 고정 수입을 원하시는 대표님',
    },
    {
      num: '02',
      title: '일시불 지급',
      subtitle: '최대 수억 원 임대료 일시불 선지급',
      icon: CreditCard,
      points: [
        '20년 치 임대료 일시불 선지급 정산',
        '사업 자금 · 부채 상환용 대규모 목돈 즉시 확보',
        '시공 완료 즉시 약정 임대료 지급',
        '개인 / 법인 맞춤 세무 솔루션 지원',
      ],
      recommendation: '사업 확장이나 즉시 활용 가능한 목돈이 필요하신 대표님',
    },
    {
      num: '03',
      title: '임대수익 미리 받기',
      subtitle: '1년 · 3년 · 5년 단위 스마트 선지급',
      icon: Clock,
      points: [
        '1년 / 3년 / 5년 단위 유연한 선지급 선택',
        '자금 계획에 맞춘 탄력적 스케줄 운영',
        '정산 주기(월납 / 연납 / 선지급) 변경 가능',
        '실시간 투명한 정산 내역 확인',
      ],
      recommendation: '자금 집행 및 현금 흐름을 계획적으로 운용하실 대표님',
    },
    {
      num: '04',
      title: '지붕 수리비 지원',
      subtitle: '노후 지붕 방수 & 수리 시공비 지원',
      icon: Wrench,
      points: [
        '지붕 방수 및 누수 보수 시공비 전폭 지원',
        '1등급 전문 방수 시공팀 투입 (누수 완벽 차단)',
        '여름철 실내 온도 3~5℃ 하강 (냉방비 절감)',
        '공장 · 축사 노후 지붕 개선 및 자산 가치 상승',
      ],
      recommendation: '지붕 누수 및 수리비 부담을 해결하고 싶으신 대표님',
    },
  ];

  // Lock body scroll when modal is active
  useEffect(() => {
    if (selectedBenefit) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedBenefit]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedBenefit(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const scrollToConsultation = () => {
    setSelectedBenefit(null);
    const elem = document.getElementById('consultation');
    if (elem) {
      elem.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="benefits" className="py-20 sm:py-28 bg-slate-50 text-slate-900 relative border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <ScrollReveal direction="up">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight mb-5 leading-snug keep-all">
              지붕임대 <span className="text-sky-600 underline decoration-sky-300 underline-offset-8">정말 편안한 수익</span>
            </h2>
            
            {/* Core Quote Box */}
            <div className="p-6 rounded-2xl bg-white border border-slate-200 text-slate-800 text-base sm:text-lg font-bold leading-relaxed shadow-sm keep-all max-w-2xl mx-auto">
              "단순한 공간 임대가 아닙니다. <br />
              <span className="text-sky-600 font-extrabold">지붕만 있으면 든든한 수익</span>까지 안겨드립니다."
            </div>
          </div>
        </ScrollReveal>

        {/* Featured Big Metric Banner */}
        <ScrollReveal direction="up">
          <div className="mb-10 sm:mb-12 relative rounded-2xl sm:rounded-3xl bg-gradient-to-r from-sky-600 via-sky-500 to-blue-600 p-6 sm:p-10 text-white shadow-xl overflow-hidden text-center group">
            <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
              <ShieldCheck className="w-64 h-64 text-white" />
            </div>

            <div className="relative z-10 max-w-3xl mx-auto space-y-2.5 text-center">
              <h3 className="text-xl sm:text-4xl lg:text-5xl font-black text-white leading-tight tracking-tight keep-all drop-shadow text-center">
                놀고 있는 2,000평 지붕 <br />
                <span className="text-yellow-300">최대 10억 원의 확정 수익!</span>
              </h3>
              <p className="text-xs sm:text-sm text-sky-100 font-medium keep-all">
                방치되어 세금만 부담되던 공장·축사·창고 지붕이 매달 매년 고정 임대료를 가져다주는 효자 자산으로 변화합니다.
              </p>
            </div>
          </div>
        </ScrollReveal>

        {/* 4 Core Benefit Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-10 sm:mb-12">
          {benefitCards.map((item, idx) => {
            const IconComp = item.icon;
            return (
              <ScrollReveal key={idx} direction="up" delay={idx * 0.1}>
                <div
                  onClick={() => setSelectedBenefit(item)}
                  className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-7 border border-slate-200/80 shadow-sm sm:shadow-md hover:border-sky-400 hover:shadow-xl transition-all duration-300 flex flex-col justify-between h-full group cursor-pointer hover:-translate-y-1"
                >
                  <div>
                    {/* Header: Icon & Num */}
                    <div className="flex items-center justify-between mb-4 sm:mb-5">
                      <div className="p-3 sm:p-3.5 rounded-xl sm:rounded-2xl bg-sky-50 border border-sky-200 text-sky-600 group-hover:bg-sky-500 group-hover:text-white transition-all shadow-xs">
                        <IconComp className="w-5 h-5 sm:w-6 sm:h-6" />
                      </div>
                      <span className="text-[11px] sm:text-xs font-black px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                        {item.num}
                      </span>
                    </div>

                    {/* Title & Description */}
                    <h4 className="text-lg sm:text-xl font-black text-slate-900 mb-1.5 sm:mb-2 tracking-tight group-hover:text-sky-600 transition-colors">
                      {item.title}
                    </h4>
                    <p className="text-xs sm:text-sm font-extrabold text-slate-700 leading-relaxed keep-all">
                      {item.subtitle}
                    </p>
                  </div>

                  {/* Card Footer */}
                  <div className="mt-5 pt-3.5 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-sky-600 group-hover:text-sky-700 transition-colors">
                    <span>혜택 상세 보기</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        {/* Bottom Callout CTA */}
        <ScrollReveal direction="up">
          <div className="text-center">
            <button
              onClick={scrollToConsultation}
              className="w-full sm:w-auto justify-center inline-flex items-center gap-2.5 px-7 py-4 rounded-xl sm:rounded-2xl bg-sky-500 hover:bg-sky-600 text-white font-black text-xs sm:text-base transition-all shadow-lg shadow-sky-500/25 active:scale-95 cursor-pointer group"
            >
              <span>내 지붕 임대 수익 1:1 무료 계산하기</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </ScrollReveal>

      </div>

      {/* Benefit Detail Modal Popup */}
      {selectedBenefit && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-md animate-in fade-in duration-200"
          onClick={() => setSelectedBenefit(null)}
        >
          <div
            className="relative w-full max-w-lg bg-white rounded-3xl p-6 sm:p-8 shadow-2xl border border-slate-200 overflow-hidden max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedBenefit(null)}
              className="absolute top-5 right-5 p-2 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
              aria-label="닫기"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Modal Header */}
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3.5 rounded-2xl bg-sky-50 text-sky-600 border border-sky-200 shadow-sm shrink-0">
                {React.createElement(selectedBenefit.icon, { className: 'w-7 h-7' })}
              </div>
              <div className="space-y-1 pr-6">
                <h3 className="text-2xl font-black text-slate-900 tracking-tight">
                  {selectedBenefit.title}
                </h3>
                <p className="text-xs sm:text-sm font-bold text-slate-600">
                  {selectedBenefit.subtitle}
                </p>
              </div>
            </div>

            {/* Bullet Points List */}
            <div className="space-y-3 mb-6">
              <h4 className="text-xs font-black text-slate-700 uppercase tracking-wider">
                주요 혜택 및 상세 내용
              </h4>
              <div className="space-y-2.5">
                {selectedBenefit.points.map((pt, pIdx) => (
                  <div key={pIdx} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700 leading-relaxed keep-all">
                    <CheckCircle2 className="w-4 h-4 text-sky-500 shrink-0 mt-0.5" />
                    <span>{pt}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recommended Target Box */}
            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-600 leading-relaxed keep-all mb-6">
              <strong>추천 대상:</strong> {selectedBenefit.recommendation}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2.5">
              <button
                onClick={scrollToConsultation}
                className="flex-1 py-3.5 px-5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-black text-xs sm:text-sm shadow-md shadow-sky-500/25 active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                <span>이 혜택으로 1:1 맞춤 견적 신청</span>
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setSelectedBenefit(null)}
                className="py-3.5 px-5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs sm:text-sm border border-slate-200 transition-colors cursor-pointer"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
